import { genericMemo, toBoolean } from '@mnd-frontend/ts';
import { GREY_400 } from '@mnd-frontend/ui';
import { scaleBand } from 'd3-scale';
import React, { type ReactNode } from 'react';
import styled from 'styled-components';
import { Legend, PositionRelative, SvgChart } from '../lib/components';
import { Grid } from '../lib/components/Graph';
import { BottomMonths, BottomStrict } from '../lib/components/Graph/Axes/Bottom';
import Left from '../lib/components/Graph/Axes/Left';
import { ContextProvider } from '../lib/context';

import { sum } from 'lodash-es';
import { GraphWrapper } from '../lib/components/GraphWrapper';
import { useScaleBandX, useScaleLinearY } from '../lib/hooks/d3';
import { useColorAndId, useFilterUnselected } from '../lib/hooks/data';
import { max } from '../lib/utils';
import type { DataPoint, Scale } from '../types';

const ZeroLine = styled.line`
  stroke: ${GREY_400};
  stroke-width: 1px;
`;

type ExtendedDataPoint = DataPoint & {
  group?: number | string;
  month?: number;
};

const GraphStacked = <T extends ExtendedDataPoint>({
  data,
  mappings,
  showMonths,
  onBarClick,
}: {
  data: T[];
  mappings: {
    name: string | ReactNode;
    dataKey: keyof Omit<T, 'name'>;
    color: string;
    id: string;
  }[];
  showMonths?: boolean;
  onBarClick?: (item: any) => void;
}) => {
  const filteredMappings = useFilterUnselected(mappings);
  const maxValue = max(data, d => {
    return sum(filteredMappings.map(m => d[m.dataKey]));
  });

  const scaleX = useScaleBandX(data, 0.3, 0.2);
  const scaleY = useScaleLinearY(0, maxValue);
  const [x0, x1] = scaleX.range();
  const yZero = scaleY(0);
  const [y0] = scaleY.range();

  return (
    <PositionRelative>
      <SvgChart>
        <Grid scaleX={scaleX} scaleY={scaleY} />
        <StackedBars
          mappings={filteredMappings}
          data={data}
          scaleY={scaleY}
          drawXAxis
          onBarClick={onBarClick}
        />
        <Left scale={scaleY} x={x0} includeFirstTick />
        <ZeroLine y1={yZero} y2={yZero} x1={x0} x2={x1} />
        <ZeroLine y1={y0} y2={y0} x1={x0} x2={x1} />
        {showMonths && <BottomMonths data={data} y={y0} extraPadding={24} />}
      </SvgChart>
    </PositionRelative>
  );
};
const StackedBars = <T extends ExtendedDataPoint>({
  data,
  mappings,
  scaleY,
  drawXAxis,
  onBarClick,
}: {
  drawXAxis?: boolean;
  scaleY: Scale;
  data: T[];
  mappings: {
    name: string | ReactNode;
    dataKey: keyof Omit<T, 'name'>;
    color: string;
    id: string;
    detailsDataKey?: keyof T;
  }[];
  onBarClick?: (item: any) => void;
}) => {
  const [y0] = scaleY.range();

  const renderData = data.flatMap((item, i) => {
    let offsetValue = 0;
    const row = mappings.map(mapping => {
      const ret = {
        id: mapping.id + '_' + i,
        color: mapping.color,
        offsetValue: offsetValue,
        value: Number(item[mapping.dataKey]),
        name: item.name,
        group: item.group,
        details:
          typeof mapping.detailsDataKey === 'string'
            ? (item[mapping.detailsDataKey] as unknown)
            : undefined,
      };
      offsetValue += Number(item[mapping.dataKey]);
      return ret;
    });
    return row;
  });

  const uniqueGroupIds = [...new Set(renderData.map(i => i.group))].filter(toBoolean);
  const hasGroups = uniqueGroupIds.length > 1;

  const groupedData = hasGroups
    ? uniqueGroupIds.map(id => {
        return renderData.filter(i => i.group === id);
      })
    : [renderData];

  const scaleX = useScaleBandX(groupedData, hasGroups ? 0.1 : 0, hasGroups ? 0.1 : 0.01);
  const outerWidth = scaleX.bandwidth();

  return (
    <>
      {groupedData.map((group, i) => {
        const outerX = scaleX(i.toString()) || 0;
        const innerScale = scaleBand()
          .domain(group.map((_d, i) => i.toString()))
          .range([outerX, outerWidth + outerX])
          .paddingInner(0.1)
          .paddingOuter(0);

        const xDomains = group
          .filter((_d, i) => i % mappings.length === 0)
          .map(({ name }) => ({ name }));
        const xAxisScale = scaleBand()
          .domain(xDomains.map((_d, i) => i.toString()))
          .range([outerX, outerWidth + outerX])
          .paddingInner(0.1)
          .paddingOuter(0);

        const width = innerScale.bandwidth() * mappings.length;

        return (
          <React.Fragment key={i}>
            {drawXAxis && <BottomStrict scale={xAxisScale as any} y={y0} data={xDomains} />}
            {group.map((item, j) => {
              const { id, color, value, offsetValue } = item;
              const jj = Math.floor(j / mappings.length) * mappings.length;
              const offesetValue = (scaleY(0) || 0) - (scaleY(offsetValue) || 0);
              const x = innerScale(jj.toString()) || 0;
              const y = (scaleY(value) || 0) - offesetValue;
              const height = (scaleY(0) || 0) - y - offesetValue;

              return (
                <React.Fragment key={id}>
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={color}
                    cursor={onBarClick && item?.details ? 'pointer' : undefined}
                    onClick={() => onBarClick?.(item?.details)}
                  />
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

export const StackedBarChart = genericMemo(
  <T extends DataPoint>({
    data,
    height = 320,
    mappings,
    legend = true,
    showMonths,
    onBarClick,
  }: {
    data: T[];
    height?: number;
    mappings: {
      name: string | ReactNode;
      dataKey: Extract<keyof Omit<T, 'name'>, string>;
      color?: string;
    }[];
    legend?: boolean;

    showMonths?: boolean;
    onBarClick?: (item: any) => void;
  }) => {
    return (
      <ContextProvider minWidth={200} height={height} selectToggle hideSelected>
        <Inner
          mappings={mappings}
          legend={legend}
          data={data}
          showMonths={showMonths}
          onBarClick={onBarClick}
        />
      </ContextProvider>
    );
  },
);

const Inner = <T extends DataPoint>({
  mappings,
  legend,
  data,
  showMonths,
  onBarClick,
}: {
  data: T[];
  mappings: {
    name: string | ReactNode;
    dataKey: Extract<keyof Omit<T, 'name'>, string>;
    color?: string;
  }[];
  legend?: boolean;
  showMonths?: boolean;
  onBarClick?: (item: any) => void;
}) => {
  const coloredMappings = useColorAndId(mappings);
  return (
    <GraphWrapper>
      {legend && <Legend items={coloredMappings} />}
      <GraphStacked
        mappings={coloredMappings}
        data={data}
        showMonths={showMonths}
        onBarClick={onBarClick}
      />
    </GraphWrapper>
  );
};
